import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Printer } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppSelector } from "../../../store/hooks";

import "../../../style/orderPrint.css";
import { getOrderDetail } from "../../../services/orders-api";

const DEFAULT_ORDER = {
  orderNumber: "ORD-1024",
  createdAt: "2025-11-05T09:30:00Z",
  customerName: "Anthony William Anderson",
  totalAmount: 2340.0,
  items: [],
};

const formatCurrency = (value) => `€${(Number(value) || 0).toFixed(2)}`;

const OrderViewPrint = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const hasPrintedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const { isPartsURL, isAccessoriesURL } = useAppSelector(
    (state) => state.domain
  );
  useEffect(() => {
    const fetchOrderDetail = async () => {
      try {
        setLoading(true);
        const data = await getOrderDetail(id);
        setOrderData(data);
      } catch (error) {
        console.error("Error fetching order:", error);
        toast.error("Failed to fetch order details");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOrderDetail();
    }
  }, [id]);

  const order = orderData || DEFAULT_ORDER;

  const products = order.items || [];

  useEffect(() => {
    if (!hasPrintedRef.current && orderData && !loading) {
      const timer = setTimeout(() => {
        window.print();
        hasPrintedRef.current = true;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [orderData, loading]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="order-print-container">
        <div className="text-center p-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="order-print-container">
        <div className="text-center p-5">
          <h3>Order not found</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="order-print-container">
      <div className="order-print-header">
        <button
          className={` ${
            isPartsURL
              ? "order-print-back-btn-parts"
              : isAccessoriesURL
              ? "order-print-back-btn-acc"
              : "order-print-back-btn"
          }`}
          onClick={handleBack}
        >
          <ArrowLeft size={15} />
          Back to Order
        </button>
        <button
          className={` ${
            isPartsURL
              ? "order-print-print-btn-parts"
              : isAccessoriesURL
              ? "order-print-print-btn-acc"
              : "order-print-print-btn"
          }`}
          onClick={handlePrint}
        >
          <Printer size={16} />
          Print
        </button>
      </div>

      <div
        className={`${
          isPartsURL
            ? "order-print-content-parts"
            : isAccessoriesURL
            ? "order-print-content-acc"
            : "order-print-content"
        }`}
      >
        <div className="order-print-info-name">
          <div className="order-print-customer-name">{order.customer_name}</div>

          <div className="order-print-customer-info">
            {order.items_count} Items | {order.order_code} |{" "}
            {formatCurrency(order.total_amount)}
          </div>
        </div>

        <div className="order-print-order-table">
          <div className="order-print-table-header">
            <div className="order-print-col-product">PRODUCT</div>
            <div className="order-print-col-product">PRICE</div>
            <div className="order-print-col-qty">QTY.</div>
            <div className="order-print-col-total">TOTAL</div>
          </div>

          <div className="order-print-table-body">
            {products.map((item) => (
              <div
                key={item.id}
                className={`order-print-table-row ${
                  item.isOutOfStock ? "order-print-out-of-stock-row" : ""
                }`}
              >
                <div className="order-print-col-product">
                <div className="order-product-info">
                    {item.product_detail?.image ? (
                      <img
                        src={item.product_detail.image}
                        alt={item.product_detail?.name}
                        className="order-product-image"
                      />
                    ) : (
                      <div className="order-product-placeholder">
                        <span className="order-placeholder-text">
                          {item.product_detail?.name || "Product"}
                        </span>
                      </div>
                    )}
                    <div className="order-product-details">
                      <p className="order-product-name">
                        {item.product_detail?.name}
                        {item.product_detail?.suffix &&
                          ` ${item.product_detail.suffix}`}
                      </p>
                      <p
                        className="order-product-name"
                        style={{
                          fontSize: "12px",
                          fontWeight: "400",
                          marginTop: "-4px",
                          color: "#8C959F",
                        }}
                      >
                        {[
                          item.product_detail?.ram,
                          item.product_detail?.storage,
                          ...(item.product_detail?.variants?.map((v) =>
                            typeof v === "object"
                              ? Object.values(v).join(" ")
                              : v
                          ) || []),
                          item.product_detail?.used_mobile ? "Used" : "New",
                        ]
                          .filter(Boolean)
                          .join(" | ")}
                      </p>
                      <span
                        className={` ${
                          isPartsURL
                            ? "order-condition-badge-parts"
                            : isAccessoriesURL
                            ? "order-condition-badge-acc"
                            : "order-condition-badge"
                        } ${item.product_detail?.used_mobile ? "used" : "new"}`}
                      >
                        {item.product_detail?.used_mobile ? "Used" : "New"}
                      </span>
                      {item.status === "out_of_stock" && (
                        <div className="order-out-of-stock-info">
                          <p className="order-not-included-text">
                            <span className={`${isPartsURL ? "order-removed-from-inventory-parts" : isAccessoriesURL ? "order-removed-from-inventory-acc" : "order-removed-from-inventory"}`}>
                              Removed from inventory
                            </span>
                            Not included in total.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="order-print-col-price-value">
                  {formatCurrency(item.unit_price)}
                </div>
                <div className="order-print-col-qty-value">
                  <span className="order-print-qty-value">{item.quantity}</span>
                </div>
                <div className="order-print-col-total-value">
                  {formatCurrency(item.line_total)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderViewPrint;
